/**
 * Los tres estados del tema y la mecanica minima para leerlos, guardarlos y
 * aplicarlos. Sin React a proposito: el mismo contrato lo usan el proveedor y
 * el script de arranque de `index.html`, que corre antes de que exista React.
 */

/**
 * OJO: los tres valores, la clave de `localStorage` y el valor por defecto
 * estan repetidos en el script de arranque de `index.html`, que corre antes de
 * que exista ningun modulo y por eso no puede importarlos. Si cambia algo de
 * esto, hay que cambiarlo alli tambien.
 */
export const TEMAS = ['claro', 'oscuro', 'sistema'] as const

export type Tema = (typeof TEMAS)[number]

/**
 * Claro por defecto, como el resto de aplicativos Trazabis (Proyectos,
 * Contable). El oscuro y el del sistema estan a un clic y la eleccion se
 * guarda, asi que quien lo prefiera solo lo cambia una vez por navegador.
 */
export const TEMA_POR_DEFECTO: Tema = 'claro'

/** Misma familia de claves que el token de sesion (`tactica.token`). */
export const CLAVE_TEMA = 'tactica.tema'

/** Atributo del elemento raiz. Es el que `graficos/paleta.ts` ya espera. */
export const ATRIBUTO_TEMA = 'data-theme'

/**
 * Clase de Trazabis DS en `<html>` con el tema ya RESUELTO (claro u oscuro).
 * Es la que enciende los tokens oscuros de `styles/tz-system.css`.
 */
export const CLASE_OSCURO = 'tz-dark'

export const ETIQUETAS: Record<Tema, string> = {
  claro: 'Claro',
  oscuro: 'Oscuro',
  sistema: 'Sistema',
}

export function esTema(valor: unknown): valor is Tema {
  return typeof valor === 'string' && (TEMAS as readonly string[]).includes(valor)
}

/**
 * El tema guardado, o el de por defecto. Cualquier fallo de `localStorage`
 * -modo privado de Safari, almacenamiento bloqueado por politica- se traga y
 * devuelve el valor por defecto: quedarse sin tema no puede tumbar el panel.
 */
export function leerTemaGuardado(): Tema {
  try {
    const valor = window.localStorage.getItem(CLAVE_TEMA)
    return esTema(valor) ? valor : TEMA_POR_DEFECTO
  } catch {
    return TEMA_POR_DEFECTO
  }
}

export function guardarTema(tema: Tema): void {
  try {
    window.localStorage.setItem(CLAVE_TEMA, tema)
  } catch {
    // Sin persistencia el tema vale para esta pestana y ya. Preferible a
    // reventar al pulsar un boton.
  }
}

/** `true` si el sistema operativo pide oscuro. Sin `matchMedia` (pruebas), claro. */
export function sistemaOscuro(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

/** Claro u oscuro, ya resuelto el estado `sistema`. */
export function temaResuelto(tema: Tema): 'claro' | 'oscuro' {
  if (tema === 'sistema') return sistemaOscuro() ? 'oscuro' : 'claro'
  return tema
}

export function aplicarTema(tema: Tema): void {
  const raiz = document.documentElement
  raiz.setAttribute(ATRIBUTO_TEMA, tema)
  raiz.classList.toggle(CLASE_OSCURO, temaResuelto(tema) === 'oscuro')
}
